import { Divider, Flex, Tooltip } from "@mantine/core";
import { type Dispatch, type SetStateAction } from "react";
import { useNavigate } from "react-router-dom";
import { LogName, NoEntries } from "src/pages/Home/Home.styled";
import { type TLogbookStatus } from "src/pages/Home/Home.types";
import { MEDLBID } from "src/utils";

import { CardContainer } from "./LogbookCard.styled";
import LogbookCardDetails from "./LogbookCardDetails";
import { DateTimeDisp } from "../DateTime";
import { ReviewText } from "src/components/pages/Summary/ReviewsTable/ReviewsTable.styled";

interface ILogbookCard {
  lbid: string;
  open: number;
  lbname: string;
  lastentry?: string;
  lastentryDate?: string;
  lastentryTime?: string;
  lastentryOffset?: string;
  lbtype: string;
  restricted?: boolean;
  userAllowed?: boolean;
  setClickedLbid?: Dispatch<SetStateAction<any>>;
  openAuthModal?: any;
  showPreview?: boolean;
  status: TLogbookStatus | null;
  description?: string;
  lbjpg?: string;
  latest?: any;
  geLockedLog?: boolean;
  reverify?: number;
}

const LogbookCard = ({
  lbid,
  open,
  lbname,
  latest,
  lastentry = "",
  lastentryDate,
  lastentryTime,
  lastentryOffset,
  lbtype,
  restricted = false,
  userAllowed = true,
  setClickedLbid,
  openAuthModal,
  geLockedLog,
  ...details
}: ILogbookCard) => {
  const navigate = useNavigate();
  const getDate = () => {
    let date = { utc: "", lt: "", offset: "" };
    date.lt = latest?.entrydate;
    date.utc = latest?.entrydateUTC; //  ?? latest?.datecreated removed due to discrepencies
    if (date.utc) date.offset = latest?.datecreatedOffset;
    if (!date.lt && !date.utc && lastentry) {
      date.lt = lastentry;
    }

    return date;
  };

  if (lbname.endsWith("regulations 2006") && lbname.includes("regulation 11")) {
    lbname =
      "Certificate Of Thorough Examination of Lifting Appliance/Derricks/Loose Gear/Wire Rope, Regulation 11.";
  }
  const SPECIAL_COLLECTIONS = [
    "8c1b02d8-3f89-11f0-8c47-0a900ab7ef27",
    "b8bb6278-5cbd-11f0-8651-02c1fe2db5a5",
  ];

  const handleClickLogbookCard = () => {
    if (geLockedLog) {
      openAuthModal();
      return;
    }
    setClickedLbid && setClickedLbid(lbid);
    let entryID = latest
      ? latest.partid
        ? latest.partid
        : latest.lbeid
          ? latest.lbeid
          : latest.meid
            ? latest.meid
            : null
      : null;
    // return;
    if (userAllowed && restricted) openAuthModal();
    else if (userAllowed)
      if (lbtype === "logbook") {
        if (lbid === MEDLBID) {
          navigate(`/logbook/${lbid}/meid/${entryID}`);
        } else {
          navigate(`/logbook/${lbid}/lbeid/${entryID}`);
        }
      } else if (lbtype === "part") {
        navigate(`/logbook/${lbid}/partid/${entryID}`);
      } else if (lbtype === "collection") {
        if (SPECIAL_COLLECTIONS.includes(lbid)) {
          navigate(`/collection/${lbid}/custom-dashboard`);
        } else {
          navigate(`/collection/${lbid}`);
        }
      }
  };

  const date = getDate();

  return (
    <Tooltip
      color="dark"
      label={lbname}
      disabled={!lbname || lbname?.length <= 45}>
      <CardContainer
        data-cy={lbtype === "collection" ? "collection-card" : "logbook-card"}
        onClick={handleClickLogbookCard}
        open={Boolean(open)}
        userAllowed={userAllowed ? "true" : "false"}
        restricted={restricted ? "true" : "false"}
        style={{ opacity: geLockedLog ? 0.6 : 1 }}>
        <LogName data-cy="card-title">{lbname}</LogName>
        <Divider size={2} />
        {date ? (
          <Flex mt={6} mb={6} align="flex-start" gap={8}>
            <ReviewText>Last Entry:</ReviewText>
            <DateTimeDisp
              color="blue"
              utc={date.utc}
              lt={date.lt}
              utcOffset={date.offset}
              mergeDate
            />
          </Flex>
        ) : (
          <NoEntries>No entries</NoEntries>
        )}
        <LogbookCardDetails
          {...details}
          geLockedLog={geLockedLog}
          lbid={lbid}
          open={open}
          userAllowed={userAllowed}
          restricted={restricted}
        />
      </CardContainer>
    </Tooltip>
  );
};

export default LogbookCard;
